/* Admin — Store Master (CRUD, CSV bulk upload, retailer→store mapping w/ history). */
import { useEffect, useState } from "react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Upload, Plus, Trash2, History, MapPin } from "lucide-react";

const ADMIN_ROLES = new Set(["admin", "spykar_admin", "system_admin"]);

export default function AdminStoreMaster() {
    const { user } = useAuth();
    const [stores, setStores] = useState([]);
    const [users, setUsers] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [history, setHistory] = useState([]);
    const [newStore, setNewStore] = useState({ name: "", code: "", city: "", zone: "" });
    const [mapForm, setMapForm] = useState({ retailer_id: "", store_code: "", effective_from: "", remarks: "" });
    const [tab, setTab] = useState("stores");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const [s, u, m, h] = await Promise.all([
                http.get("/admin/store-master"),
                http.get("/admin/users"),
                http.get("/admin/store-master/mappings"),
                http.get("/admin/store-master/mappings/history"),
            ]);
            setStores(s.data); setUsers(u.data); setMappings(m.data); setHistory(h.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };
    useEffect(() => { if (user && ADMIN_ROLES.has(user.role)) load(); }, [user]);

    const addStore = async () => {
        if (!newStore.name || !newStore.code || !newStore.city || !newStore.zone) {
            toast.error("Fill all fields"); return;
        }
        setBusy(true);
        try {
            await http.post("/admin/store-master", newStore);
            toast.success("Saved");
            setNewStore({ name: "", code: "", city: "", zone: "" });
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const deleteStore = async (code) => {
        if (!window.confirm(`Delete store ${code}?`)) return;
        try { await http.delete(`/admin/store-master/${code}`); toast.success("Deleted"); await load(); }
        catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };
    const upload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const r = await http.post("/admin/store-master/bulk", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Uploaded: ${r.data.created} created, ${r.data.updated} updated`);
            await load();
        } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
        finally { setBusy(false); e.target.value = ""; }
    };
    const addMapping = async () => {
        if (!mapForm.retailer_id || !mapForm.store_code || !mapForm.effective_from) {
            toast.error("Fill retailer, store, effective date"); return;
        }
        setBusy(true);
        try {
            await http.post("/admin/store-master/mappings", mapForm);
            toast.success("Mapping saved");
            setMapForm({ retailer_id: "", store_code: "", effective_from: "", remarks: "" });
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (!user) return null;
    if (!ADMIN_ROLES.has(user.role)) {
        return <div className="p-8 text-center text-muted-foreground">Admin only.</div>;
    }
    const retailers = users.filter((u) => u.role === "retailer");

    return (
        <div className="mx-auto max-w-6xl space-y-5 pb-16" data-testid="admin-store-master">
            <div>
                <h1 className="text-[22px] font-semibold tracking-tight">Store Master</h1>
                <div className="mt-0.5 text-[12px] text-muted-foreground">
                    Manage store list and retailer↔store mappings with relocation history.
                </div>
            </div>

            <div className="flex gap-1">
                {["stores", "mappings", "history"].map((t) => (
                    <button key={t} onClick={() => setTab(t)} data-testid={`sm-tab-${t}`}
                        className={`rounded-md border px-4 py-2 text-[12px] font-semibold ${tab === t ? "border-teal-600 bg-teal-50 text-teal-700 dark:bg-teal-950/30" : "border-border bg-card"}`}>
                        {t.toUpperCase()}
                    </button>
                ))}
            </div>

            {tab === "stores" && (
                <>
                    <div className="grid grid-cols-2 gap-2 rounded-md border border-border bg-card p-4 sm:grid-cols-5">
                        <input placeholder="Name" data-testid="sm-name" value={newStore.name}
                            onChange={(e) => setNewStore({ ...newStore, name: e.target.value })}
                            className="rounded-md border border-border bg-background px-3 py-2 text-[13px]" />
                        <input placeholder="Code" data-testid="sm-code" value={newStore.code}
                            onChange={(e) => setNewStore({ ...newStore, code: e.target.value })}
                            className="rounded-md border border-border bg-background px-3 py-2 text-[13px]" />
                        <input placeholder="City" data-testid="sm-city" value={newStore.city}
                            onChange={(e) => setNewStore({ ...newStore, city: e.target.value })}
                            className="rounded-md border border-border bg-background px-3 py-2 text-[13px]" />
                        <input placeholder="Zone" data-testid="sm-zone" value={newStore.zone}
                            onChange={(e) => setNewStore({ ...newStore, zone: e.target.value })}
                            className="rounded-md border border-border bg-background px-3 py-2 text-[13px]" />
                        <button onClick={addStore} disabled={busy} data-testid="sm-add"
                            className="inline-flex items-center justify-center gap-1 rounded-md bg-teal-600 px-3 py-2 text-[12px] font-semibold text-white">
                            <Plus className="h-3.5 w-3.5" /> Add / Update
                        </button>
                    </div>
                    <div className="flex items-center gap-2 rounded-md border border-dashed border-teal-500/40 bg-teal-50/40 p-3 dark:bg-teal-950/20">
                        <Upload className="h-4 w-4 text-teal-700" />
                        <span className="text-[12px] font-medium text-teal-700">CSV bulk upload</span>
                        <span className="text-[11px] text-muted-foreground">Columns: name, code, city, zone</span>
                        <input type="file" accept=".csv" onChange={upload} data-testid="sm-csv"
                            className="ml-auto text-[11px]" />
                    </div>
                    <div className="overflow-hidden rounded-md border border-border bg-card">
                        <table className="w-full text-[12px]">
                            <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-2 text-left">Code</th>
                                    <th className="px-3 py-2 text-left">Name</th>
                                    <th className="px-3 py-2 text-left">City</th>
                                    <th className="px-3 py-2 text-left">Zone</th>
                                    <th className="px-3 py-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stores.map((s) => (
                                    <tr key={s.id} className="border-t border-border">
                                        <td className="px-3 py-1.5 font-mono">{s.code}</td>
                                        <td className="px-3 py-1.5">{s.name}</td>
                                        <td className="px-3 py-1.5">{s.city}</td>
                                        <td className="px-3 py-1.5">{s.zone}</td>
                                        <td className="px-3 py-1.5 text-right">
                                            <button onClick={() => deleteStore(s.code)} data-testid={`sm-del-${s.code}`}
                                                className="rounded-sm p-1 text-red-500 hover:bg-red-50">
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {stores.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No stores yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {tab === "mappings" && (
                <>
                    <div className="grid grid-cols-1 gap-2 rounded-md border border-border bg-card p-4 sm:grid-cols-5">
                        <select value={mapForm.retailer_id} onChange={(e) => setMapForm({ ...mapForm, retailer_id: e.target.value })}
                            data-testid="sm-map-retailer" className="rounded-md border border-border bg-background px-3 py-2 text-[13px]">
                            <option value="">Select retailer</option>
                            {retailers.map((r) => (<option key={r.id} value={r.id}>{r.name} — {r.email}</option>))}
                        </select>
                        <select value={mapForm.store_code} onChange={(e) => setMapForm({ ...mapForm, store_code: e.target.value })}
                            data-testid="sm-map-store" className="rounded-md border border-border bg-background px-3 py-2 text-[13px]">
                            <option value="">Select store</option>
                            {stores.map((s) => (<option key={s.id} value={s.code}>{s.code} — {s.name}</option>))}
                        </select>
                        <input type="date" data-testid="sm-map-date" value={mapForm.effective_from}
                            onChange={(e) => setMapForm({ ...mapForm, effective_from: e.target.value })}
                            className="rounded-md border border-border bg-background px-3 py-2 text-[13px]" />
                        <input placeholder="Remarks" data-testid="sm-map-remarks" value={mapForm.remarks}
                            onChange={(e) => setMapForm({ ...mapForm, remarks: e.target.value })}
                            className="rounded-md border border-border bg-background px-3 py-2 text-[13px]" />
                        <button onClick={addMapping} disabled={busy} data-testid="sm-map-add"
                            className="inline-flex items-center justify-center gap-1 rounded-md bg-teal-600 px-3 py-2 text-[12px] font-semibold text-white">
                            <MapPin className="h-3.5 w-3.5" /> Map
                        </button>
                    </div>
                    <div className="overflow-hidden rounded-md border border-border bg-card">
                        <table className="w-full text-[12px]">
                            <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-2 text-left">Retailer</th>
                                    <th className="px-3 py-2 text-left">Store</th>
                                    <th className="px-3 py-2 text-left">City</th>
                                    <th className="px-3 py-2 text-left">Effective From</th>
                                    <th className="px-3 py-2 text-left">Effective To</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mappings.map((m) => (
                                    <tr key={m.id} className="border-t border-border">
                                        <td className="px-3 py-1.5">{m.retailer_name}</td>
                                        <td className="px-3 py-1.5">{m.store_name}</td>
                                        <td className="px-3 py-1.5">{m.store_city}</td>
                                        <td className="px-3 py-1.5">{m.effective_from}</td>
                                        <td className="px-3 py-1.5">{m.effective_to || <span className="text-emerald-600">active</span>}</td>
                                    </tr>
                                ))}
                                {mappings.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No mappings yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {tab === "history" && (
                <div className="overflow-hidden rounded-md border border-border bg-card">
                    <div className="border-b border-border bg-muted/40 px-3 py-2 text-[11px] font-semibold uppercase text-muted-foreground">
                        <History className="mr-1 inline h-3.5 w-3.5" /> Full audit log
                    </div>
                    <table className="w-full text-[12px]">
                        <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2 text-left">At</th>
                                <th className="px-3 py-2 text-left">Event</th>
                                <th className="px-3 py-2 text-left">Retailer</th>
                                <th className="px-3 py-2 text-left">Store</th>
                                <th className="px-3 py-2 text-left">Effective</th>
                                <th className="px-3 py-2 text-left">Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((h) => (
                                <tr key={h.id} className="border-t border-border">
                                    <td className="px-3 py-1.5 text-muted-foreground">{h.at?.slice(0, 19)}</td>
                                    <td className="px-3 py-1.5">{h.event}</td>
                                    <td className="px-3 py-1.5">{h.retailer_name}</td>
                                    <td className="px-3 py-1.5">{h.store_name}</td>
                                    <td className="px-3 py-1.5">{h.effective_from} → {h.effective_to || "—"}</td>
                                    <td className="px-3 py-1.5 text-muted-foreground">{h.remarks || "—"}</td>
                                </tr>
                            ))}
                            {history.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No history yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
